import { useState, useMemo } from 'react'
import { useParams, Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { getEAProject, getEATasks, cancelEATask, retryEATask } from '../services/ea-api'
import type { EAPipelineTaskListItem, EAProjectPhase } from '../types/ea'
import EATaskCard from '../components/ea/EATaskCard'

const glassPanel = {
  background: 'rgba(15, 23, 42, 0.6)',
  backdropFilter: 'blur(20px)',
  border: '1px solid rgba(100, 180, 255, 0.1)',
}

const PHASE_ORDER: EAProjectPhase[] = ['discovery', 'design', 'standards_gate', 'deployment', 'qc', 'deliverables', 'complete']

const PHASE_LABELS: Record<string, string> = {
  discovery: 'Discovery',
  design: 'Design',
  standards_gate: 'Standards Gate',
  deployment: 'Deployment',
  qc: 'QC',
  deliverables: 'Deliverables',
  complete: 'Complete',
}

const VENDOR_COLORS: Record<string, string> = {
  Arista: '#2563eb',
  PaloAlto: '#dc2626',
  Nutanix: '#059669',
  Azure: '#0284c7',
  Cisco: '#7c3aed',
}

const TASK_TYPE_OPTIONS: { value: string; label: string }[] = [
  { value: 'all', label: 'All Types' },
  { value: 'orchestrator', label: 'Orchestrator' },
  { value: 'vendor_pipeline', label: 'Vendor Pipeline' },
  { value: 'requirements', label: 'Requirements' },
  { value: 'design', label: 'Design' },
  { value: 'standards_gate', label: 'Standards Gate' },
  { value: 'deployment', label: 'Deployment' },
  { value: 'qc', label: 'QC' },
  { value: 'deliverables_promotion', label: 'Deliverables' },
]

const STATUS_OPTIONS: { value: string; label: string }[] = [
  { value: 'all', label: 'All Status' },
  { value: 'pending', label: 'Pending' },
  { value: 'queued', label: 'Queued' },
  { value: 'running', label: 'Running' },
  { value: 'completed', label: 'Completed' },
  { value: 'failed', label: 'Failed' },
  { value: 'blocked', label: 'Blocked' },
  { value: 'cancelled', label: 'Cancelled' },
]

type PhaseStatus = 'pending' | 'active' | 'complete'

function getPhaseStatus(phase: EAProjectPhase, currentPhase: EAProjectPhase, tasks: EAPipelineTaskListItem[]): PhaseStatus {
  const currentIndex = PHASE_ORDER.indexOf(currentPhase)
  const phaseIndex = PHASE_ORDER.indexOf(phase)

  // Check if there are tasks in this phase
  const phaseTasks = tasks.filter((t) => {
    // Map task_type to phase
    const taskPhaseMap: Record<string, string> = {
      orchestrator: 'discovery',
      vendor_pipeline: 'discovery',
      requirements: 'discovery',
      design: 'design',
      standards_gate: 'standards_gate',
      deployment: 'deployment',
      qc: 'qc',
      deliverables_promotion: 'deliverables',
    }
    return taskPhaseMap[t.task_type] === phase
  })

  const hasRunning = phaseTasks.some((t) => t.status === 'running' || t.status === 'queued')
  const allComplete = phaseTasks.length > 0 && phaseTasks.every((t) => t.status === 'completed')

  if (allComplete || phaseIndex < currentIndex) return 'complete'
  if (hasRunning || phaseIndex === currentIndex) return 'active'
  return 'pending'
}

const PHASE_STATUS_COLORS: Record<PhaseStatus, string> = {
  pending: '#4b5563',
  active: '#3b82f6',
  complete: '#10b981',
}

export default function EAPipelineView() {
  const { projectId } = useParams<{ projectId: string }>()
  const queryClient = useQueryClient()
  const [vendorFilter, setVendorFilter] = useState('all')
  const [statusFilter, setStatusFilter] = useState('all')
  const [typeFilter, setTypeFilter] = useState('all')

  const { data: project, isLoading: projectLoading } = useQuery({
    queryKey: ['ea-project', projectId],
    queryFn: () => getEAProject(projectId!).then((res) => res.data),
    enabled: !!projectId,
  })

  const { data: tasksData, isLoading: tasksLoading } = useQuery({
    queryKey: ['ea-tasks', projectId, statusFilter, typeFilter],
    queryFn: () =>
      getEATasks(projectId!, {
        status: statusFilter !== 'all' ? statusFilter : undefined,
        task_type: typeFilter !== 'all' ? typeFilter : undefined,
      }).then((res) => res.data),
    enabled: !!projectId,
    refetchInterval: 5000,
  })

  const tasks: EAPipelineTaskListItem[] = Array.isArray(tasksData) ? tasksData : []

  const cancelMutation = useMutation({
    mutationFn: (taskId: string) => cancelEATask(taskId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ea-tasks', projectId] })
    },
  })

  const retryMutation = useMutation({
    mutationFn: (taskId: string) => retryEATask(taskId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ea-tasks', projectId] })
    },
  })

  // Extract unique vendors from tasks + project
  const allVendors = useMemo(() => {
    const vendors = new Set<string>()
    if (project) project.vendors.forEach((v) => vendors.add(v))
    tasks.forEach((t) => { if (t.vendor) vendors.add(t.vendor) })
    return Array.from(vendors).sort()
  }, [project, tasks])

  // Filter tasks by vendor (client-side since API may not support it)
  const filteredTasks = useMemo(() => {
    if (vendorFilter === 'all') return tasks
    return tasks.filter((t) => t.vendor === vendorFilter || !t.vendor)
  }, [tasks, vendorFilter])

  // Group tasks by vendor, then sort by sequence
  const groupedTasks = useMemo(() => {
    const groups: Record<string, EAPipelineTaskListItem[]> = { _global: [] }
    allVendors.forEach((v) => { groups[v] = [] })

    filteredTasks.forEach((t) => {
      const key = t.vendor || '_global'
      if (!groups[key]) groups[key] = []
      groups[key].push(t)
    })

    // Sort each group by sequence_order
    for (const key of Object.keys(groups)) {
      groups[key].sort((a, b) => a.sequence_order - b.sequence_order)
    }

    return groups
  }, [filteredTasks, allVendors])

  const hasRunningTasks = tasks.some((t) => t.status === 'running')

  if (projectLoading || tasksLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div
            className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center"
            style={{
              background: 'rgba(59, 130, 246, 0.1)',
              border: '2px solid rgba(59, 130, 246, 0.3)',
              boxShadow: '0 0 30px rgba(59, 130, 246, 0.3)',
            }}
          >
            <i className="fa-solid fa-spinner fa-spin text-2xl" style={{ color: '#60a5fa' }}></i>
          </div>
          <p style={{ color: 'rgba(148, 163, 184, 0.8)' }}>Loading pipeline...</p>
        </div>
      </div>
    )
  }

  if (!project) {
    return (
      <div className="text-center py-20">
        <div
          className="w-16 h-16 rounded-xl flex items-center justify-center mx-auto mb-4"
          style={{ background: 'rgba(239, 68, 68, 0.1)', border: '1px solid rgba(239, 68, 68, 0.2)' }}
        >
          <i className="fa-solid fa-exclamation-triangle text-3xl" style={{ color: '#ef4444' }}></i>
        </div>
        <h3 className="text-lg font-semibold mb-2" style={{ color: '#e2e8f0' }}>Project not found</h3>
        <Link
          to="/ea/projects"
          className="px-6 py-3 rounded-xl text-sm font-medium inline-flex items-center space-x-2 transition-all duration-200 hover:scale-105"
          style={{
            background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.3) 0%, rgba(139, 92, 246, 0.3) 100%)',
            border: '1px solid rgba(96, 165, 250, 0.4)',
            color: '#e2e8f0',
          }}
        >
          <i className="fa-solid fa-arrow-left"></i>
          <span>Back to Projects</span>
        </Link>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-center space-x-4">
          <Link
            to={`/ea/projects/${projectId}`}
            className="w-10 h-10 rounded-xl flex items-center justify-center transition-all duration-200 hover:scale-105"
            style={{
              background: 'rgba(30, 41, 59, 0.5)',
              border: '1px solid rgba(100, 180, 255, 0.15)',
              color: 'rgba(148, 163, 184, 0.8)',
            }}
          >
            <i className="fa-solid fa-arrow-left"></i>
          </Link>
          <div>
            <div className="flex items-center space-x-3 mb-1">
              <h1 className="text-2xl font-bold" style={{ color: '#e2e8f0' }}>Pipeline</h1>
              {hasRunningTasks && (
                <span
                  className="px-2.5 py-1 text-xs font-medium rounded-full animate-pulse flex items-center space-x-1.5"
                  style={{
                    background: 'rgba(245, 158, 11, 0.15)',
                    color: '#f59e0b',
                    border: '1px solid rgba(245, 158, 11, 0.3)',
                  }}
                >
                  <span className="w-1.5 h-1.5 rounded-full" style={{ background: '#f59e0b' }} />
                  <span>Running</span>
                </span>
              )}
            </div>
            <p className="text-sm" style={{ color: 'rgba(148, 163, 184, 0.7)' }}>
              {project.project_name}
            </p>
          </div>
        </div>
        <Link
          to={`/ea/projects/${projectId}/task`}
          className="px-5 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 flex items-center space-x-2 hover:scale-105"
          style={{
            background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.3) 0%, rgba(139, 92, 246, 0.3) 100%)',
            border: '1px solid rgba(96, 165, 250, 0.4)',
            color: '#e2e8f0',
          }}
        >
          <i className="fa-solid fa-plus"></i>
          <span>Submit New Task</span>
        </Link>
      </div>

      {/* Phase Pipeline Bar */}
      <div className="rounded-xl p-6" style={glassPanel}>
        <p className="text-xs font-semibold uppercase tracking-wider mb-4" style={{ color: 'rgba(100, 116, 139, 0.6)' }}>
          Phase Pipeline
        </p>
        <div className="flex items-center justify-between">
          {PHASE_ORDER.map((phase, i) => {
            const status = getPhaseStatus(phase, project.current_phase, tasks)
            const color = PHASE_STATUS_COLORS[status]
            return (
              <div key={phase} className="flex items-center flex-1">
                <div className="flex flex-col items-center flex-1">
                  <div
                    className={`w-8 h-8 rounded-full flex items-center justify-center mb-2 ${status === 'active' ? 'ring-4' : ''}`}
                    style={{
                      background: status === 'complete'
                        ? color
                        : status === 'active'
                        ? `${color}33`
                        : `${color}22`,
                      border: `2px solid ${color}`,
                      boxShadow: `0 0 0 3px ${color}33`,
                    }}
                  >
                    {status === 'complete' ? (
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="white" strokeWidth={3}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                      </svg>
                    ) : status === 'active' ? (
                      <div className="w-3 h-3 rounded-full animate-pulse" style={{ background: color }} />
                    ) : (
                      <div className="w-2.5 h-2.5 rounded-full" style={{ background: color }} />
                    )}
                  </div>
                  <span
                    className="text-xs font-medium text-center"
                    style={{ color: status === 'pending' ? 'rgba(148, 163, 184, 0.4)' : color }}
                  >
                    {PHASE_LABELS[phase]}
                  </span>
                </div>
                {i < PHASE_ORDER.length - 1 && (
                  <div
                    className="h-0.5 flex-shrink-0 w-full max-w-[60px] -mt-6"
                    style={{
                      background: status === 'complete'
                        ? '#10b981'
                        : 'rgba(100, 116, 139, 0.2)',
                    }}
                  />
                )}
              </div>
            )
          })}
        </div>
      </div>

      {/* Filter Bar */}
      <div className="rounded-xl p-4" style={glassPanel}>
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center space-x-3">
            <span className="text-sm font-medium" style={{ color: 'rgba(148, 163, 184, 0.8)' }}>
              Filters:
            </span>
            {/* Vendor filter */}
            <div className="flex items-center space-x-1.5">
              <button
                onClick={() => setVendorFilter('all')}
                className="px-3 py-1.5 rounded-lg text-xs font-medium transition-all duration-200"
                style={{
                  background: vendorFilter === 'all' ? 'rgba(139, 92, 246, 0.25)' : 'rgba(30, 41, 59, 0.5)',
                  border: `1px solid ${vendorFilter === 'all' ? 'rgba(139, 92, 246, 0.5)' : 'rgba(100, 180, 255, 0.15)'}`,
                  color: vendorFilter === 'all' ? '#a78bfa' : 'rgba(148, 163, 184, 0.7)',
                }}
              >
                All
              </button>
              {allVendors.map((vendor) => {
                const color = VENDOR_COLORS[vendor] || '#94a3b8'
                const isActive = vendorFilter === vendor
                return (
                  <button
                    key={vendor}
                    onClick={() => setVendorFilter(vendor)}
                    className="px-3 py-1.5 rounded-lg text-xs font-medium transition-all duration-200"
                    style={{
                      background: isActive ? `${color}33` : 'rgba(30, 41, 59, 0.5)',
                      border: `1px solid ${isActive ? `${color}66` : 'rgba(100, 180, 255, 0.15)'}`,
                      color: isActive ? color : 'rgba(148, 163, 184, 0.7)',
                    }}
                  >
                    {vendor}
                  </button>
                )
              })}
            </div>

            {/* Status filter */}
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-3 py-1.5 rounded-lg text-xs"
              style={{
                background: 'rgba(30, 41, 59, 0.5)',
                border: '1px solid rgba(100, 180, 255, 0.15)',
                color: '#e2e8f0',
              }}
              aria-label="Filter by status"
            >
              {STATUS_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>

            {/* Type filter */}
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="px-3 py-1.5 rounded-lg text-xs"
              style={{
                background: 'rgba(30, 41, 59, 0.5)',
                border: '1px solid rgba(100, 180, 255, 0.15)',
                color: '#e2e8f0',
              }}
              aria-label="Filter by task type"
            >
              {TASK_TYPE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>

          <span className="text-xs" style={{ color: 'rgba(148, 163, 184, 0.5)' }}>
            {filteredTasks.length} task{filteredTasks.length !== 1 ? 's' : ''}
            {hasRunningTasks && ' · Auto-refreshing every 5s'}
          </span>
        </div>
      </div>

      {/* Task Cards grouped by vendor */}
      {filteredTasks.length > 0 ? (
        <div className="space-y-8">
          {/* Global / orchestrator tasks (no vendor) */}
          {groupedTasks._global && groupedTasks._global.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold uppercase tracking-wider mb-3" style={{ color: 'rgba(100, 116, 139, 0.6)' }}>
                <i className="fa-solid fa-globe mr-2"></i>
                Orchestrator Tasks
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {groupedTasks._global.map((task) => (
                  <EATaskCard
                    key={task.id}
                    task={task}
                    onCancel={cancelMutation.isPending ? undefined : (id) => cancelMutation.mutate(id)}
                    onRetry={retryMutation.isPending ? undefined : (id) => retryMutation.mutate(id)}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Per-vendor task groups */}
          {allVendors.map((vendor) => {
            const vendorTasks = groupedTasks[vendor]
            if (!vendorTasks || vendorTasks.length === 0) return null
            const color = VENDOR_COLORS[vendor] || '#94a3b8'
            return (
              <div key={vendor}>
                <h3 className="text-sm font-semibold uppercase tracking-wider mb-3 flex items-center space-x-2">
                  <span className="w-2.5 h-2.5 rounded-full" style={{ background: color }} />
                  <span style={{ color }}>{vendor}</span>
                  <span style={{ color: 'rgba(148, 163, 184, 0.4)' }}>
                    ({vendorTasks.length} task{vendorTasks.length !== 1 ? 's' : ''})
                  </span>
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {vendorTasks.map((task) => (
                    <EATaskCard
                      key={task.id}
                      task={task}
                      onCancel={cancelMutation.isPending ? undefined : (id) => cancelMutation.mutate(id)}
                      onRetry={retryMutation.isPending ? undefined : (id) => retryMutation.mutate(id)}
                    />
                  ))}
                </div>
              </div>
            )
          })}
        </div>
      ) : (
        /* Empty state */
        <div className="rounded-xl p-12 text-center" style={glassPanel}>
          <div
            className="w-16 h-16 rounded-xl flex items-center justify-center mx-auto mb-4"
            style={{ background: 'rgba(59, 130, 246, 0.1)', border: '1px solid rgba(59, 130, 246, 0.2)' }}
          >
            <i className="fa-solid fa-timeline text-3xl" style={{ color: 'rgba(96, 165, 250, 0.5)' }}></i>
          </div>
          <h3 className="text-lg font-semibold mb-2" style={{ color: '#e2e8f0' }}>No pipeline tasks yet</h3>
          <p className="mb-6" style={{ color: 'rgba(148, 163, 184, 0.7)' }}>
            Submit your first task to start the EA pipeline for this project.
          </p>
          <Link
            to={`/ea/projects/${projectId}/task`}
            className="px-6 py-3 rounded-xl text-sm font-medium inline-flex items-center space-x-2 transition-all duration-200 hover:scale-105"
            style={{
              background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.3) 0%, rgba(139, 92, 246, 0.3) 100%)',
              border: '1px solid rgba(96, 165, 250, 0.4)',
              color: '#e2e8f0',
            }}
          >
            <i className="fa-solid fa-paper-plane"></i>
            <span>Submit First Task</span>
          </Link>
        </div>
      )}
    </div>
  )
}
